/**
 * L5a — bandeau solennel aux jalons T13 / T30 / T46.
 *
 * Quand le moteur émet une headline `BILAN ·`, le store la dépose dans
 * `activeCheckpoint`. Ce composant l'affiche en pleine largeur, sur fond
 * ocre, avec typographie monumentale. L'utilisateur le ferme en cliquant
 * (ou il s'auto-ferme après 12 sec) pour reprendre le jeu.
 *
 * Objectif (cf. project_sprint_tier1_accessibility) : casser le fleuve
 * sans rive du mandat en chapitrant la durée. Le ressenti devient
 * « j'ai bouclé l'année 1 » plutôt que « j'ai joué 13 tours ».
 *
 * Sprint lisibilité (2026-06-10) : la variante BILAN ajoute une rangée de
 * chiffres clés — valeur actuelle + évolution depuis le checkpoint
 * précédent — pour ancrer le récit dans les indicateurs. L'audit notait
 * « les checkpoints sont du texte libre sans un seul chiffre ».
 */

import { useEffect } from "react";
import { useGame } from "../store/useGame";
import { formatDeltaValue, INDICATORS } from "../engine/indicators";

const AUTO_DISMISS_MS = 12_000;

/** Indicateurs récapitulés au bilan — les 4 structurants du mandat. */
const CHECKPOINT_INDICATOR_IDS = [
  "approval",
  "budget",
  "debt_ratio",
  "unemployment",
];

/** Jalons BILAN — pour retrouver le checkpoint précédent. */
const MILESTONES = [1, 13, 30, 46];

function CheckpointStats() {
  const history = useGame((s) => s.indicatorHistory);
  const turn = useGame((s) => s.worldState?.turn ?? 0);

  // Checkpoint précédent : dernier jalon strictement avant le tour courant.
  const sinceTurn = [...MILESTONES].reverse().find((m) => m < turn) ?? 1;

  const stats = CHECKPOINT_INDICATOR_IDS.flatMap((id) => {
    const def = INDICATORS.find((d) => d.id === id);
    const entries = history[id] ?? [];
    if (!def || entries.length === 0) return [];
    const current = entries[entries.length - 1];
    // Première entrée au tour >= sinceTurn (l'historique couvre le mandat).
    const baseline = entries.find((e) => e.turn >= sinceTurn) ?? entries[0];
    const delta = current.value - baseline.value;
    return [
      {
        id,
        label: def.label,
        value: `${def.format(current.value)}${def.unit}`,
        delta: formatDeltaValue(def, delta),
        direction: delta > 0 ? "up" : delta < 0 ? "down" : "flat",
        sinceTurn: baseline.turn,
      },
    ];
  });

  if (stats.length === 0) return null;

  return (
    <div className="checkpoint-stats">
      {stats.map((s) => (
        <div key={s.id} className="checkpoint-stat">
          <span className="checkpoint-stat-label">{s.label}</span>
          <span className="checkpoint-stat-value">{s.value}</span>
          {s.delta && (
            <span
              className="checkpoint-stat-delta"
              data-direction={s.direction}
            >
              {s.delta} depuis T{s.sinceTurn}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export function CheckpointBanner() {
  const checkpoint = useGame((s) => s.activeCheckpoint);
  const dismiss = useGame((s) => s.dismissCheckpoint);

  useEffect(() => {
    if (!checkpoint) return;
    const t = window.setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [checkpoint, dismiss]);

  if (!checkpoint) return null;

  // Europe S2 — variante visuelle UE détectée via le préfixe 🇪🇺 dans
  // la source. Fond bleu institutionnel au lieu d'ocre BILAN.
  const isEu = (checkpoint.source ?? "").startsWith("🇪🇺");

  return (
    <div
      className="checkpoint-backdrop"
      data-variant={isEu ? "eu" : "bilan"}
      role="dialog"
      aria-modal="true"
      onClick={dismiss}
    >
      <div
        className="checkpoint-card"
        data-variant={isEu ? "eu" : "bilan"}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="checkpoint-kicker">{checkpoint.source ?? "BILAN"}</div>
        <p className="checkpoint-text">{checkpoint.text}</p>
        {!isEu && <CheckpointStats />}
        <button type="button" className="checkpoint-dismiss" onClick={dismiss}>
          Reprendre le mandat →
        </button>
      </div>
    </div>
  );
}
